import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Stack,
  Typography,
} from "@mui/material";
import { PageContainer } from "@/features/ui";
import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";

import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";
import {
  GeneralInformationSection,
  AccountTransactionsSection,
} from "@/features/journal-vouchers/journal-voucher";
import { apiOutletClient, getErrorMessage } from "@/features/shared";
import { CenteredLoading, CustomDialogTitle, useSnackbar } from "@/features/ui";

export const JournalVoucher = () => {
  const { referenceNumber } = useParams();
  const navigate = useNavigate();
  const { outlet, hasPermission } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [journalVoucher, setJournalVoucher] = useState(null);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadJournalVoucher = useCallback(async () => {
    if (!outlet) return;

    setLoading(true);
    setError(null);
    try {
      const {
        data: { data },
      } = await apiOutletClient.get(`journal-vouchers/${referenceNumber}`, {
        params: { withOutlet: true },
      });
      setJournalVoucher(data);
    } catch (error) {
      setError(
        getErrorMessage(error, "Gagal memuat jurnal")
      );
    } finally {
      setLoading(false);
    }
  }, [outlet, referenceNumber]);

  useEffect(() => {
    loadJournalVoucher();
  }, [loadJournalVoucher]);

  const handleDelete = async () => {
    if (!journalVoucher || !outlet) return;

    try {
      setIsDeleting(true);
      await apiOutletClient.delete(`journal-vouchers/${referenceNumber}`, {
        headers: {
          "Outlet-Id": outlet.id,
        },
      });
      showSnackbar("Jurnal umum berhasil dihapus", "success");
      navigate("/general-ledger/journal-vouchers");
    } catch (error) {
      // Show error message from backend or fallback message
      showSnackbar(getErrorMessage(error, "Gagal menghapus jurnal umum"));
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const toolbarContent = (
    <>
      {hasPermission(PermissionEnum.DELETE_JOURNAL_VOUCHERS) && (
        <Button
          fullWidth
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => setDeleteDialogOpen(true)}
        >
          Hapus
        </Button>
      )}
      {hasPermission(PermissionEnum.UPDATE_JOURNAL_VOUCHERS) && (
        <Button
          fullWidth
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => navigate("edit")}
        >
          Ubah
        </Button>
      )}
    </>
  );

  if (loading) {
    return <CenteredLoading sx={{ height: "100vh" }} />;
  }

  if (error) {
    return (
      <Typography variant="h6" color="error">
        {error}
      </Typography>
    );
  }

  return (
    <PageContainer
      maxWidth="xl"
      title="Jurnal Umum"
      breadcrumbs={[
        { title: "Jurnal Umum", path: "/general-ledger/journal-vouchers" },
        { title: referenceNumber },
      ]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
    >
      <Stack spacing={2}>
        <GeneralInformationSection journalVoucher={journalVoucher} />
        <AccountTransactionsSection
          outletChartOfAccountTransactions={
            journalVoucher.outletChartOfAccountTransactions
          }
        />

        {/* Delete Confirmation Dialog */}
        {deleteDialogOpen && (
          <Dialog
            open
            onClose={() => !isDeleting && setDeleteDialogOpen(false)}
            maxWidth="xs"
            fullWidth
          >
            <CustomDialogTitle
              title="Hapus Jurnal Umum"
              onClose={() => !isDeleting && setDeleteDialogOpen(false)}
            />
            <DialogContent>
              <DialogContentText>
                Apakah Anda yakin ingin menghapus jurnal umum{" "}
                <strong>{referenceNumber}</strong>?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setDeleteDialogOpen(false)}
                disabled={isDeleting}
              >
                Batal
              </Button>
              <Button
                onClick={handleDelete}
                variant="contained"
                color="error"
                disabled={isDeleting}
              >
                {isDeleting ? "Menghapus..." : "Hapus"}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Stack>
    </PageContainer>
  );
};
